import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_BASE from '../config';
import { formatRelativeInTimeZone, useDisplayTimeZone } from '../lib/displayTimezone';

function formatDuration(seconds) {
  const n = parseInt(seconds, 10);
  if (!n || isNaN(n)) return '0:00';
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}


function formatPhoneNumber(num) {
  if (!num) return 'Unknown';
  const d = num.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return num;
}

function VoicemailCard({ vm, onDelete }) {
  const displayTz = useDisplayTimeZone();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef(null);

  const audioUrl = vm.recording_sid
    ? `${API_BASE}/api/voicemails/${encodeURIComponent(vm.recording_sid)}/audio`
    : null;

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const ct = audioRef.current.currentTime;
    const dur = audioRef.current.duration || 0;
    setCurrentTime(ct);
    setDuration(dur);
    setProgress(dur > 0 ? (ct / dur) * 100 : 0);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const callerDisplay = formatPhoneNumber(vm.from_number);
  const avatarLetter = (vm.from_number || '?')[0];
  const hasTranscription = vm.transcription_text && vm.transcription_text.trim().length > 0;
  const transcriptionPending = vm.transcription_status === 'pending' || vm.transcription_status === 'transcribing';
  const displayDuration = formatDuration(vm.duration || vm.duration_seconds);

  return (
    <div className="voicemail-card">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleTimeUpdate}
        onError={() => setPlaying(false)}
      />

      {/* Caller row */}
      <div className="vm-caller-row">
        <div className="vm-avatar">{avatarLetter}</div>
        <div className="vm-caller-info">
          <div className="vm-number">{callerDisplay}</div>
          <div className="vm-time">{formatRelativeInTimeZone(vm.created_at, displayTz)}</div>
        </div>
        <span className="vm-duration">{displayDuration}</span>
      </div>

      {/* Custom audio player */}
      {audioUrl && (
        <div className="vm-player">
          <button className="vm-play-btn" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? '⏸' : '▶'}
          </button>
          <div className="vm-progress" onClick={handleProgressClick}>
            <div className="vm-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="vm-time-label">{formatDuration(Math.floor(currentTime))}</span>
        </div>
      )}

      {/* Transcription */}
      {hasTranscription ? (
        <div
          className={`vm-transcription${!expanded ? ' clamped' : ''}`}
          onClick={() => setExpanded(!expanded)}
          style={{ cursor: 'pointer' }}
        >
          &ldquo;{vm.transcription_text}&rdquo;
        </div>
      ) : transcriptionPending ? (
        <div className="vm-transcription pending">Transcription pending&hellip;</div>
      ) : null}

      {/* Action row */}
      <div className="vm-actions">
        <button
          className="vm-action-btn delete"
          onClick={() => onDelete(vm)}
          aria-label={`Delete voicemail from ${callerDisplay}`}
        >
          🗑 Delete
        </button>
      </div>
    </div>
  );
}

export default function Voicemail({ authToken, onUnheardCount }) {
  const [voicemails, setVoicemails] = useState([]);

  // Fetch voicemails
  useEffect(() => {
    const fetchVMs = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/voicemails`, {
          headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' }
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setVoicemails(list);
          if (onUnheardCount) onUnheardCount(list.length);
        }
      } catch (e) { console.error('[VM] fetch error', e); }
    };
    fetchVMs();
    const interval = setInterval(fetchVMs, 10000);
    return () => clearInterval(interval);
  }, [authToken, onUnheardCount]);

  const deleteVoicemail = useCallback(async (vm) => {
    const label = vm.from_number || 'Unknown';
    if (!window.confirm(`Delete voicemail from ${label}? This also removes the Twilio recording.`)) return;

    // Optimistic update
    setVoicemails((prev) => prev.filter((x) => x.id !== vm.id));
    if (onUnheardCount) onUnheardCount(voicemails.length - 1);

    try {
      const res = await fetch(`${API_BASE}/api/voicemails/${vm.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' }
      });
      if (!res.ok) {
        console.error('[VM] delete failed', res.status);
        // Reinstate on failure
        setVoicemails((prev) => [vm, ...prev].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        ));
        if (onUnheardCount) onUnheardCount(voicemails.length);
      }
    } catch (err) {
      console.error('[VM] delete error', err);
      setVoicemails((prev) => [vm, ...prev].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      ));
      if (onUnheardCount) onUnheardCount(voicemails.length);
    }
  }, [authToken, onUnheardCount, voicemails.length]);

  return (
    <div className="voicemail-tab">
      <div className="voicemail-header">
        <h2>Voicemail</h2>
        <span className="voicemail-count">{voicemails.length}</span>
      </div>
      <div className="voicemail-list">
        {voicemails.length === 0 ? (
          <div className="voicemail-empty">
            <span className="empty-icon">📭</span>
            <p>No voicemails</p>
          </div>
        ) : (
          voicemails.map(vm => (
            <VoicemailCard
              key={vm.id || vm.recording_sid}
              vm={vm}
              onDelete={deleteVoicemail}
            />
          ))
        )}
      </div>
    </div>
  );
}
